import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../../../environments/environment';

import {
	ClaimUsageIngestionAcceptedResponse,
	ClaimUsageIngestionBlobStatus,
	ClaimUsageIngestionDetectedMetadata,
	ClaimUsageIngestionErrorsResponse,
	ClaimUsageIngestionHistoryItem,
	ClaimUsageIngestionJobStatus,
	ClaimUsageIngestionOperator,
	ClaimUsageIngestionStatusResponse,
	ClaimUsageIngestionUploadRequest,
} from './claim-usage-ingestion.models';

export const CLAIM_USAGE_INGESTION_ENDPOINT = '/claim-usage-ingestions';
export const CLAIM_USAGE_INGESTION_STATUS_ENDPOINT =
	'/claim-usage-ingestions/:jobId';
export const CLAIM_USAGE_INGESTION_ERRORS_ENDPOINT =
	'/claim-usage-ingestions/:jobId/errors';
export const CLAIM_USAGE_INGESTION_DOWNLOAD_ENDPOINT =
	'/claim-usage-ingestions/:jobId/download';

export abstract class ClaimUsageIngestionService {
	abstract download(jobId: string): Observable<HttpResponse<Blob>>;

	abstract getErrors(
		jobId: string,
		limit?: number,
		offset?: number,
	): Observable<ClaimUsageIngestionErrorsResponse>;

	abstract getStatus(
		jobId: string,
	): Observable<ClaimUsageIngestionStatusResponse>;

	abstract list(
		organizationId: string,
	): Observable<ClaimUsageIngestionHistoryItem[]>;

	abstract upload(
		request: ClaimUsageIngestionUploadRequest,
	): Observable<ClaimUsageIngestionAcceptedResponse>;
}

interface ClaimUsageIngestionApiResponse {
	detectedMetadata?: ClaimUsageIngestionDetectedMetadata;
	fileId: string;
	jobId: string;
	queuedAt: string;
	status: ClaimUsageIngestionApiStatus;
}

type ClaimUsageIngestionApiStatus =
	| 'AWAITING_PROCESSING'
	| 'COMPLETED'
	| 'CREATED'
	| 'ERROR'
	| 'FAILED'
	| 'FAILURE'
	| 'IN_PROGRESS'
	| 'PARTIALLY_COMPLETED'
	| 'PENDING'
	| 'PROCESSING'
	| 'QUEUED'
	| 'SUCCEEDED'
	| 'SUCCESS';

interface ClaimUsageIngestionApiJobResponse {
	competence?: string;
	detectedBillingCompetence?: string;
	detectedMetadata?: ClaimUsageIngestionDetectedMetadata;
	errorCode?: string;
	errorMessage?: string;
	errorSummary?: string;
	failedRows?: number;
	fileId?: string;
	fileName?: string;
	files?: ClaimUsageIngestionApiFileResponse[];
	finishedAt?: string;
	jobId: string;
	operator?: ClaimUsageIngestionOperator | string;
	operatorName?: string;
	organization?: string;
	organizationId?: string;
	organizationName?: string;
	primaryFile?: {
		detectedMetadata?: ClaimUsageIngestionDetectedMetadata;
		fileId?: string;
		fileName?: string;
		originalName?: string;
		progressStatus?: string;
		status?: ClaimUsageIngestionApiStatus;
		storagePath?: string;
	};
	processedRows?: number;
	progressStatus?: string;
	responsible?: string;
	startedAt?: string;
	status?: ClaimUsageIngestionApiStatus;
	totalRows?: number;
	uploadedAt?: string;
	uploadedBy?: string;
	uploadedByName?: string;
	userName?: string;
}

interface ClaimUsageIngestionApiFileResponse {
	detectedMetadata?: ClaimUsageIngestionDetectedMetadata;
	fileId?: string;
	fileName?: string;
	operatorName?: string;
	originalName?: string;
	progressStatus?: string;
	status?: ClaimUsageIngestionApiStatus;
	storagePath?: string;
	uploadedBy?: string;
}

interface ClaimUsageIngestionApiListResponse {
	items: ClaimUsageIngestionApiJobResponse[];
	limit: number;
	offset: number;
	total: number;
}

@Injectable()
export class ClaimUsageIngestionApiService extends ClaimUsageIngestionService {
	constructor(private readonly httpClient: HttpClient) {
		super();
	}

	download(jobId: string): Observable<HttpResponse<Blob>> {
		return this.httpClient.get(
			`${this.getQueryBaseUrl()}${CLAIM_USAGE_INGESTION_ENDPOINT}/${jobId}/download`,
			{ observe: 'response', responseType: 'blob' },
		);
	}

	getErrors(
		jobId: string,
		limit = 50,
		offset = 0,
	): Observable<ClaimUsageIngestionErrorsResponse> {
		return this.httpClient.get<ClaimUsageIngestionErrorsResponse>(
			`${this.getQueryBaseUrl()}${CLAIM_USAGE_INGESTION_ENDPOINT}/${jobId}/errors`,
			{
				params: {
					limit,
					offset,
				},
			},
		);
	}

	getStatus(jobId: string): Observable<ClaimUsageIngestionStatusResponse> {
		return this.httpClient
			.get<ClaimUsageIngestionApiJobResponse>(
				`${this.getQueryBaseUrl()}${CLAIM_USAGE_INGESTION_ENDPOINT}/${jobId}`,
			)
			.pipe(map((response) => this.mapApiStatusResponse(response)));
	}

	list(organizationId: string): Observable<ClaimUsageIngestionHistoryItem[]> {
		return this.httpClient
			.get<
				ClaimUsageIngestionApiJobResponse[] | ClaimUsageIngestionApiListResponse
			>(`${this.getQueryBaseUrl()}${CLAIM_USAGE_INGESTION_ENDPOINT}`, {
				params: organizationId ? { organizationId } : undefined,
			})
			.pipe(
				map((response) =>
					this.getApiHistoryItems(response).map((item) =>
						this.mapApiHistoryItem(item, organizationId),
					),
				),
			);
	}

	upload(
		request: ClaimUsageIngestionUploadRequest,
	): Observable<ClaimUsageIngestionAcceptedResponse> {
		const formData = new FormData();

		formData.append('file', request.file);
		formData.append('operator', request.operator);
		formData.append('competence', request.competence);
		if (request.uploadedBy) {
			formData.append('uploadedBy', request.uploadedBy);
		}

		if (request.responsible) {
			formData.append('responsible', request.responsible);
		}

		return this.httpClient
			.post<ClaimUsageIngestionApiResponse>(
				`${this.getUploadBaseUrl()}${CLAIM_USAGE_INGESTION_ENDPOINT}`,
				formData,
				{ observe: 'response' },
			)
			.pipe(
				map((response) => {
					const responseBody = response.body as ClaimUsageIngestionApiResponse;

					return {
						detectedMetadata: responseBody.detectedMetadata,
						fileId: responseBody.fileId,
						jobId: responseBody.jobId,
						status: this.mapApiStatus(responseBody.status),
						statusCode: response.status as 201 | 202,
					};
				}),
			);
	}

	private getApiHistoryItems(
		response:
			| ClaimUsageIngestionApiJobResponse[]
			| ClaimUsageIngestionApiListResponse,
	): ClaimUsageIngestionApiJobResponse[] {
		return Array.isArray(response) ? response : (response.items ?? []);
	}

	private getApiJobStatus(
		response: ClaimUsageIngestionApiJobResponse,
	): ClaimUsageIngestionApiStatus | string | undefined {
		const primaryFile = response.primaryFile;
		const firstFile = response.files?.[0];

		return (
			response.status ??
			response.progressStatus ??
			primaryFile?.status ??
			primaryFile?.progressStatus ??
			firstFile?.status ??
			firstFile?.progressStatus
		);
	}

	private getQueryBaseUrl(): string {
		return environment.claimUsageIngestionApiUrl || environment.apiUrl;
	}

	private getUploadBaseUrl(): string {
		return (
			environment.claimUsageIngestionUploadApiUrl ||
			environment.claimUsageIngestionApiUrl ||
			environment.apiUrl
		);
	}

	private mapApiHistoryItem(
		item: ClaimUsageIngestionApiJobResponse,
		fallbackOrganizationId: string,
	): ClaimUsageIngestionHistoryItem {
		const statusResponse = this.mapApiStatusResponse(item);
		const primaryFile = item.primaryFile;
		const firstFile = item.files?.[0];
		const detectedMetadata =
			item.detectedMetadata ??
			primaryFile?.detectedMetadata ??
			firstFile?.detectedMetadata;

		return {
			postProcessing: {
				error: statusResponse.errorMessage,
				errorCode: statusResponse.errorCode,
				errorLines: statusResponse.failedRows,
				errorMessage: statusResponse.errorMessage,
				errorSummary: statusResponse.errorSummary,
				failedRows: statusResponse.failedRows,
				finalStatus: statusResponse.status,
				finishedAt: statusResponse.finishedAt,
				lastUpdatedAt: statusResponse.lastUpdatedAt,
				processedLines: statusResponse.processedRows,
				processedRows: statusResponse.processedRows,
				startedAt: statusResponse.startedAt,
				totalRows: statusResponse.totalRows,
			},
			preProcessing: {
				blobStatus: ClaimUsageIngestionBlobStatus.STORED,
				competence:
					item.competence ??
					detectedMetadata?.detectedFileCompetence ??
					detectedMetadata?.detectedBillingCompetence ??
					item.detectedBillingCompetence ??
					'',
				estimatedProcessingWindow: 'Aguardando processamento',
				fileId: item.fileId ?? primaryFile?.fileId ?? firstFile?.fileId,
				jobId: item.jobId,
				jobStatus: statusResponse.status,
				operator: this.mapApiOperator(
					item.operator ?? item.operatorName ?? firstFile?.operatorName,
				),
				operatorName:
					this.mapApiOperator(
						item.operator ?? item.operatorName ?? firstFile?.operatorName,
					) ?? '',
				organizationId: item.organizationId ?? fallbackOrganizationId,
				organizationName: item.organizationName ?? item.organization ?? '',
				originalFileName:
					item.fileName ??
					primaryFile?.originalName ??
					primaryFile?.fileName ??
					firstFile?.originalName ??
					firstFile?.fileName ??
					detectedMetadata?.originalName ??
					'',
				responsible: item.responsible,
				size: detectedMetadata?.fileSize ?? 0,
				storagePath:
					detectedMetadata?.storagePath ??
					primaryFile?.storagePath ??
					firstFile?.storagePath,
				uploadedAt:
					item.uploadedAt ?? item.startedAt ?? new Date().toISOString(),
				uploadedBy:
					item.responsible ??
					item.uploadedBy ??
					item.uploadedByName ??
					item.userName ??
					firstFile?.uploadedBy ??
					'',
			},
		};
	}

	private mapApiOperator(
		operator: ClaimUsageIngestionOperator | string | null | undefined,
	): ClaimUsageIngestionOperator | undefined {
		return operator === 'CNU' || operator === 'SULAMERICA'
			? operator
			: undefined;
	}

	private mapApiStatus(
		status: ClaimUsageIngestionApiStatus | string | null | undefined,
	): ClaimUsageIngestionJobStatus {
		switch (status) {
			case 'COMPLETED':
			case 'SUCCEEDED':
			case 'SUCCESS':
				return ClaimUsageIngestionJobStatus.COMPLETED;
			case 'FAILED':
			case 'FAILURE':
			case 'ERROR':
				return ClaimUsageIngestionJobStatus.FAILED;
			case 'PARTIALLY_COMPLETED':
				return ClaimUsageIngestionJobStatus.PARTIALLY_COMPLETED;
			case 'CREATED':
				return ClaimUsageIngestionJobStatus.CREATED;
			case 'IN_PROGRESS':
			case 'PROCESSING':
				return ClaimUsageIngestionJobStatus.PROCESSING;
			case 'AWAITING_PROCESSING':
			case 'PENDING':
			case 'QUEUED':
				return ClaimUsageIngestionJobStatus.QUEUED;
			default:
				return ClaimUsageIngestionJobStatus.UNKNOWN;
		}
	}

	private mapApiStatusResponse(
		response: ClaimUsageIngestionApiJobResponse,
	): ClaimUsageIngestionStatusResponse {
		const now = new Date().toISOString();

		const mappedStatus = this.mapApiStatus(this.getApiJobStatus(response));
		const isBackendFailureInconsistency = Boolean(
			mappedStatus === ClaimUsageIngestionJobStatus.PROCESSING &&
				response.finishedAt &&
				response.errorMessage,
		);

		return {
			error: response.errorMessage,
			errorCode: response.errorCode,
			errorLines: response.failedRows ?? 0,
			errorMessage: response.errorMessage,
			errorSummary: response.errorSummary,
			failedRows: response.failedRows ?? 0,
			finishedAt: response.finishedAt,
			jobId: response.jobId,
			lastUpdatedAt: response.finishedAt ?? response.startedAt ?? now,
			processedLines: response.processedRows ?? 0,
			processedRows: response.processedRows ?? 0,
			startedAt: response.startedAt ?? '',
			status: isBackendFailureInconsistency
				? ClaimUsageIngestionJobStatus.FAILED
				: mappedStatus,
			totalRows: response.totalRows ?? 0,
		};
	}
}
